import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/d71s5ps991ove783t5eg';

const contactInfo = [
  {
    icon: 'ri-map-pin-2-line',
    label: 'Adresse',
    lines: ['23 Rue Nationale', '59800 Lille, France'],
    link: null,
  },
  {
    icon: 'ri-phone-line',
    label: 'Téléphone',
    lines: ['+33 6 39 93 63 29'],
    link: 'tel:+33639936329',
  },
  {
    icon: 'ri-mail-line',
    label: 'Email',
    lines: ['mariakrutak02@inbox.eu'],
    link: 'mailto:mariakrutak02@inbox.eu',
  },
];

type FormState = 'idle' | 'sending' | 'success' | 'error';

export default function ContactPage() {
  const [formState, setFormState] = useState<FormState>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('sending');

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const fd = new FormData(form);
    fd.forEach((value, key) => data.append(key, value as string));

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setFormState('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormState('error');
      }
    } catch {
      setFormState('error');
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Page Hero */}
      <section className="pt-36 pb-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-navy-700 mb-4 block">
            Contact
          </span>
          <h1 className="text-5xl font-bold text-navy-800 leading-tight max-w-2xl">
            Contactez-nous
          </h1>
          <p className="text-gray-500 text-lg mt-4 max-w-xl leading-relaxed">
            Échangeons sur votre projet. Nous vous répondrons dans les meilleurs délais.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">

            {/* Left – Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-navy-800 mb-2">KRUTAK</h2>
                <p className="text-gray-500 text-sm">Maria Ruta Krutak</p>
                <p className="text-gray-400 text-xs mt-1">SIRET : 999 389 893 00010</p>
              </div>

              {/* Map */}
              <div className="rounded-xl overflow-hidden border border-gray-100" style={{ height: '200px' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2530.6871938247447!2d3.0621559!3d50.6311747!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c2d5a6df4b8b85%3A0x7b0a9c5e0c7b2d1a!2s23%20Rue%20Nationale%2C%2059800%20Lille%2C%20France!5e0!3m2!1sfr!2sfr!4v1699000000000!5m2!1sfr!2sfr"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localisation KRUTAK"
                />
              </div>

              {/* Contact blocks */}
              <div className="space-y-5">
                {contactInfo.map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-navy-50 shrink-0">
                      <i className={`${item.icon} text-navy-700 text-lg`} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium mb-1 uppercase tracking-wide">
                        {item.label}
                      </p>
                      {item.lines.map((line, j) => (
                        item.link ? (
                          <a
                            key={j}
                            href={item.link}
                            className="block text-navy-800 font-medium text-[15px] hover:text-navy-600 transition-colors duration-200 cursor-pointer"
                          >
                            {line}
                          </a>
                        ) : (
                          <p key={j} className="text-navy-800 font-medium text-[15px]">{line}</p>
                        )
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right – Form */}
            <div className="lg:col-span-3">
              <div className="bg-gray-50 rounded-2xl p-10 border border-gray-100">
                <h2 className="text-2xl font-bold text-navy-800 mb-2">
                  Envoyez-nous un message
                </h2>
                <p className="text-gray-500 text-sm mb-8">
                  Tous les champs marqués d'un * sont obligatoires.
                </p>

                {formState === 'success' ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-4">
                      <i className="ri-check-line text-green-600 text-2xl" />
                    </div>
                    <h3 className="text-xl font-bold text-navy-800 mb-2">Message envoyé !</h3>
                    <p className="text-gray-500 text-sm">
                      Merci pour votre message. Nous vous répondrons dans les meilleurs délais.
                    </p>
                    <button
                      onClick={() => setFormState('idle')}
                      className="whitespace-nowrap cursor-pointer mt-6 text-sm text-navy-700 font-medium hover:underline"
                    >
                      Envoyer un autre message
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-1.5">
                          Nom *
                        </label>
                        <input
                          id="nom"
                          name="nom"
                          type="text"
                          required
                          placeholder="Votre nom complet"
                          className="w-full h-12 px-4 text-sm border border-gray-200 rounded-lg bg-white text-gray-800 focus:outline-none focus:border-navy-400 transition-colors duration-200"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                          Email *
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          placeholder="votre@email.com"
                          className="w-full h-12 px-4 text-sm border border-gray-200 rounded-lg bg-white text-gray-800 focus:outline-none focus:border-navy-400 transition-colors duration-200"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Téléphone
                      </label>
                      <input
                        id="telephone"
                        name="telephone"
                        type="tel"
                        placeholder="+33 6 00 00 00 00"
                        className="w-full h-12 px-4 text-sm border border-gray-200 rounded-lg bg-white text-gray-800 focus:outline-none focus:border-navy-400 transition-colors duration-200"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Description de la demande * ({charCount}/500)
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        onChange={(e) => setCharCount(e.target.value.length)}
                        placeholder="Décrivez votre projet ou votre besoin..."
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg bg-white text-gray-800 focus:outline-none focus:border-navy-400 transition-colors duration-200 resize-none"
                      />
                      {charCount > 450 && (
                        <p className="text-xs text-amber-600 mt-1">
                          {500 - charCount} caractère(s) restant(s)
                        </p>
                      )}
                    </div>

                    {formState === 'error' && (
                      <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                        Une erreur est survenue. Veuillez réessayer ou nous contacter directement par email.
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={formState === 'sending'}
                      className="whitespace-nowrap cursor-pointer w-full h-13 py-4 bg-navy-800 text-white text-[15px] font-medium rounded-lg hover:bg-navy-700 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {formState === 'sending' ? (
                        <>
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-loader-4-line animate-spin" />
                          </span>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          Envoyer
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-send-plane-line" />
                          </span>
                        </>
                      )}
                    </button>

                    <p className="text-xs text-gray-400 text-center">
                      Vos données sont traitées conformément à notre{' '}
                      <a href="/politique-confidentialite" className="underline hover:text-navy-700 cursor-pointer">
                        politique de confidentialité
                      </a>.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
